import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * /subscribe — Phase 1 minimal subscription page.
 *
 * Layout: plan cards + inline account form + optional candidate note.
 * The full 4-step wizard UX is deferred to Phase 4 polish; this ships
 * the critical path (plan selection → checkout → Stripe redirect) so
 * end-to-end payment can be validated immediately.
 */
export default function SubscribePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    password: '', password_confirm: '',
    candidate_note: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/subscribe/plans`);
        setPlans(res.data?.data || []);
        // Auto-select the featured plan on first load
        const feat = (res.data?.data || []).find(p => p.featured);
        if (feat) setSelectedPlanId(feat.id);
        else if (res.data?.data?.[0]) setSelectedPlanId(res.data.data[0].id);
      } catch (e) {
        setError('Could not load subscription plans. Please try again shortly.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cancelled = params.get('cancelled') === '1';

  const validate = () => {
    if (!selectedPlanId) return 'Please choose a plan.';
    if (!form.first_name.trim() || !form.last_name.trim()) return 'Please enter your name.';
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) return 'Please enter a valid email.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    if (form.password !== form.password_confirm) return 'Passwords do not match.';
    return '';
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/subscribe/start`, {
        plan_id: selectedPlanId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        candidate_note: form.candidate_note.trim(),
        origin_url: window.location.origin,
      });
      // Redirect to Stripe hosted checkout
      window.location.href = res.data?.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to start checkout. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) return (
    <Container className="py-5 text-center"><Spinner animation="border" /></Container>
  );

  return (
    <Container className="py-4" style={{ maxWidth: 960 }} data-testid="subscribe-page">
      <div className="mb-4 text-center">
        <h1 className="mb-2">Manage a Candidate Profile</h1>
        <p className="text-muted">Subscribe to unlock candidate profile management. Cancel any time.</p>
      </div>

      {cancelled && (
        <Alert variant="warning" className="mb-4">
          You cancelled the previous checkout. No charge was made. Fill in the form below to try again.
        </Alert>
      )}

      {/* Step 1: plan cards */}
      <Row className="g-3 mb-4">
        {plans.map((p) => (
          <Col md={6} key={p.id}>
            <Card
              className={`h-100 ${selectedPlanId === p.id ? 'border-primary shadow' : ''}`}
              onClick={() => setSelectedPlanId(p.id)}
              style={{ cursor: 'pointer' }}
              data-testid={`plan-card-${p.id}`}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <Card.Title className="mb-0">{p.name}</Card.Title>
                  {p.featured && <Badge bg="warning" text="dark">Recommended</Badge>}
                </div>
                <div className="mb-2">
                  {p.discount_currently_valid && p.effective_price !== p.regular_price ? (
                    <>
                      <span className="text-muted text-decoration-line-through me-2">${p.regular_price}</span>
                      <span className="fs-3 fw-bold text-success">${p.effective_price}</span>
                      <span className="text-muted small"> / {p.billing_interval}</span>
                      {p.discount_label && <Badge bg="success" className="ms-2">{p.discount_label}</Badge>}
                    </>
                  ) : (
                    <>
                      <span className="fs-3 fw-bold">${p.regular_price}</span>
                      <span className="text-muted small"> / {p.billing_interval}</span>
                    </>
                  )}
                </div>
                <p className="text-muted small">{p.description}</p>
                <ul className="small mb-0">
                  {(p.features || []).map((f, i) => (
                    <li key={i}><i className="bi bi-check2 text-success me-1" />{f}</li>
                  ))}
                </ul>
              </Card.Body>
              <Card.Footer className="text-center py-2">
                <Form.Check
                  type="radio"
                  name="plan"
                  checked={selectedPlanId === p.id}
                  onChange={() => setSelectedPlanId(p.id)}
                  label={selectedPlanId === p.id ? 'Selected' : 'Select this plan'}
                  data-testid={`plan-radio-${p.id}`}
                />
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Step 2 & 3: account + candidate note */}
      <Card className="mb-4">
        <Card.Header>Your Account</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>First name</Form.Label>
              <Form.Control value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="first-name-input" />
            </Col>
            <Col md={6}>
              <Form.Label>Last name</Form.Label>
              <Form.Control value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="last-name-input" />
            </Col>
            <Col md={6}>
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="email-input" />
            </Col>
            <Col md={6}>
              <Form.Label>Phone</Form.Label>
              <Form.Control value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="phone-input" />
            </Col>
            <Col md={6}>
              <Form.Label>Password (min 8 chars)</Form.Label>
              <Form.Control type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="password-input" />
            </Col>
            <Col md={6}>
              <Form.Label>Confirm password</Form.Label>
              <Form.Control type="password" value={form.password_confirm} onChange={e => setForm({...form, password_confirm: e.target.value})} data-testid="password-confirm-input" />
            </Col>
            <Col xs={12}>
              <Form.Label>Which candidate will you manage? <span className="text-muted small">(optional)</span></Form.Label>
              <Form.Control
                as="textarea" rows={2}
                placeholder="e.g., I'm running for Broward County Commission District 3."
                value={form.candidate_note}
                onChange={e => setForm({...form, candidate_note: e.target.value})}
                data-testid="candidate-note-input"
              />
              <Form.Text>Our team will reach out after payment to link your subscription to the right candidate profile.</Form.Text>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" data-testid="subscribe-error">{error}</Alert>}

      <div className="text-center">
        <Button
          size="lg" variant="primary"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="subscribe-checkout-btn"
        >
          {submitting && <Spinner size="sm" className="me-2" />}
          Continue to secure checkout
        </Button>
        <div className="text-muted small mt-2">
          <i className="bi bi-shield-lock me-1" />
          Payments processed by Stripe. You'll be redirected to complete the purchase.
        </div>
      </div>
    </Container>
  );
}
